import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Paper,
  Divider
} from "@mui/material";

import { ExtendedBed, useWardTypeData } from "../../../hooks/useWardTypeData";
import { useTransferBed } from "../../../hooks/query/useBedAssignments";

interface Props {
  initialBed: ExtendedBed | null;
  open: boolean;
  setOpen: (v: boolean) => void;
}

// eslint-disable-next-line import/prefer-default-export
export const TransferBedDialog = ({ initialBed, open, setOpen }: Props): React.ReactNode => {
  const { extendedBeds, wards, rooms } = useWardTypeData();
  const { mutate: transferBed, isLoading: isPosting } = useTransferBed();

  // local states
  const [remark, setRemark] = React.useState<string>("");
  const [selectedBedId, setSelectedBedId] = React.useState<number | null>(null);
  const [selectedRoomId, setSelectedRoomId] = React.useState<number | null>(null);
  const [selectedWardId, setSelectedWardId] = React.useState<number | null>(null);

  // variables for destination - find from the main arrays
  const selectedDestinationWard = wards.find((w) => Number(w.id) === selectedWardId) || null;
  const selectedDestinationRoom = rooms.find((r) => Number(r.id) === selectedRoomId) || null;
  const selectedDestinationBed = extendedBeds.find((b) => Number(b.id) === selectedBedId) || null;

  // Current bed details
  const currentBed = initialBed;
  const currentRoom =
    rooms.find((r) => Number(r.id) === currentBed?.roomId) || currentBed?.room || null;
  const currentWard =
    wards.find((w) => Number(w.id) === currentRoom?.wardId) || currentBed?.ward || null;

  // helper functions for destination
  const wardRooms = React.useMemo(() => {
    if (!selectedWardId) return [];
    return rooms.filter((r) => Number(r.wardId) === Number(selectedWardId));
  }, [rooms, selectedWardId]);

  const roomBeds = React.useMemo(() => {
    if (!selectedRoomId) return [];
    return extendedBeds.filter(
      (b) =>
        Number(b.roomId) === Number(selectedRoomId) &&
        !b.isOccupied &&
        (!initialBed || b.id !== initialBed.id)
    );
  }, [selectedRoomId, extendedBeds, initialBed]);

  const handleSave = () => {
    if (initialBed?.activeIpd?.id && selectedBedId) {
      transferBed(
        {
          ipdId: initialBed.activeIpd.id,
          bedId: selectedBedId,
          wardId: selectedWardId ?? undefined,
          roomId: selectedRoomId ?? undefined,
          remark
        },
        {
          onSuccess: () => {
            setOpen(false);
          }
        }
      );
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Transfer Patient&apos;s Bed</DialogTitle>

      <DialogContent sx={{ px: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3
          }}
        >
          {/* Current Bed Column - Left */}
          <Box>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%"
              }}
            >
              <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="text.secondary">
                Current Bed
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Ward"
                  value={
                    currentWard
                      ? `${currentWard.name || ""} ${
                          currentWard.shortCode ? `(${currentWard.shortCode})` : ""
                        }`.trim()
                      : "—"
                  }
                  disabled
                  fullWidth
                  variant="outlined"
                  slotProps={{ inputLabel: { shrink: true } }}
                />

                <TextField
                  label="Room"
                  value={
                    currentRoom
                      ? `${currentRoom.name || ""} ${
                          currentRoom.code ? `(${currentRoom.code})` : ""
                        }`.trim()
                      : "—"
                  }
                  disabled
                  fullWidth
                  variant="outlined"
                  slotProps={{ inputLabel: { shrink: true } }}
                />

                <TextField
                  label="Bed"
                  value={
                    currentBed
                      ? `${currentBed?.customName || ""} ${
                          currentBed.number ? `(${currentBed.number})` : ""
                        }`.trim()
                      : "—"
                  }
                  disabled
                  fullWidth
                  variant="outlined"
                  slotProps={{ inputLabel: { shrink: true } }}
                />

                {currentBed?.activeIpd && (
                  <TextField
                    label="Current Patient"
                    value={
                      `${currentBed.activeIpd.client?.firstName} ${currentBed.activeIpd.client?.lastName} ` ||
                      "—"
                    }
                    disabled
                    fullWidth
                    variant="outlined"
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              </Box>
            </Paper>
          </Box>

          {/* Destination Bed Column - Right */}
          <Box>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "background.paper",
                borderRadius: 2,
                height: "100%"
              }}
            >
              <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="text.secondary">
                Destination Bed
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Autocomplete
                  data-testmation="wardAutocomplete"
                  value={selectedDestinationWard}
                  onChange={(_, v) => {
                    setSelectedWardId(v ? Number(v.id) : null);
                    setSelectedRoomId(null);
                    setSelectedBedId(null);
                  }}
                  options={wards}
                  getOptionLabel={(item) =>
                    item
                      ? `${item.name || ""} ${item.shortCode ? `(${item.shortCode})` : ""}`.trim()
                      : ""
                  }
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select Ward"
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  )}
                />

                <Autocomplete
                  data-testmation="roomAutocomplete"
                  value={selectedDestinationRoom}
                  options={wardRooms}
                  getOptionLabel={(item) =>
                    item ? `${item.name || ""} ${item.code ? `(${item.code})` : ""}`.trim() : ""
                  }
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  onChange={(_, v) => {
                    setSelectedRoomId(v ? Number(v.id) : null);
                    setSelectedBedId(null);
                  }}
                  disabled={!selectedWardId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select Room"
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  )}
                />

                <Autocomplete
                  data-testmation="bedAutocomplete"
                  value={selectedDestinationBed}
                  options={roomBeds}
                  getOptionLabel={(item) =>
                    item
                      ? `${item?.customName || ""} ${item.number ? `(${item.number})` : ""}`.trim()
                      : ""
                  }
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  onChange={(_, v) => {
                    setSelectedBedId(v ? Number(v.id) : null);
                  }}
                  disabled={!selectedRoomId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select Bed"
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  )}
                />

                <TextField
                  data-testmation="conditionAtAdmission"
                  label="Remark"
                  variant="outlined"
                  placeholder="Reason for transfer"
                  multiline
                  value={remark}
                  slotProps={{ inputLabel: { shrink: true } }}
                  onChange={(e) => setRemark(e.target.value)}
                />
              </Box>
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2 }}>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={isPosting || !selectedBedId}>
          Transfer Patient
        </Button>
      </DialogActions>
    </Dialog>
  );
};
